import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@elka/ui';
import { TableSkeleton } from '../components/TableSkeleton';
import { PageLayout, PageHeader } from '@elka/layout';
import { Briefcase, FileText, TrendingUp, AlertCircle, SearchX, DollarSign } from 'lucide-react';
import { DEAL_STAGE_LABELS, DEAL_QUOTING_STATUS_LABELS, isEarlyStageDeal } from '@elka/types';
import type { DealQuotingStatus, DealStage } from '@elka/types';
import { useDealsWithQuotingStatus } from '../features/deal-workspace/hooks/useDealsWithQuotingStatus';
import type { DealWithQuotingStatus } from '../features/deal-workspace/hooks/useDealsWithQuotingStatus';
import { DealsTable } from '../features/deal-workspace/components/DealsTable';
import { FilterBar } from '../components/FilterBar';
import { DealsViewToggle } from '../components/DealsViewToggle';
import { formatNumberWithCommas } from '@elka/utils';
import {
  STAGE_CHIP_COLORS,
  QUOTING_STATUS_CHIP_COLORS,
} from '../features/deal-workspace/lib/dealStatusColors';

function StatCard({
  label,
  value,
  sub,
  iconClass,
  icon: Icon,
  delay,
}: {
  label: string;
  value: string | number;
  sub?: string;
  iconClass: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  delay: string;
}) {
  return (
    <div className={`stat-card animate-stagger-${delay}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="stat-label mb-2">{label}</p>
          <p className="stat-value tabular-nums">{value}</p>
          {sub && <p className="stat-sub mt-1">{sub}</p>}
        </div>
        <div className={`icon-badge icon-badge-lg ${iconClass} mr-3 flex-shrink-0`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
    </div>
  );
}

export function DealsPage() {
  const navigate = useNavigate();
  const { deals, dealsLoading, error } = useDealsWithQuotingStatus();
  const [search, setSearch] = useState('');
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedQuotingStatuses, setSelectedQuotingStatuses] = useState<string[]>([]);

  const stageOptions = useMemo(() => {
    return (
      Object.entries(DEAL_STAGE_LABELS)
        // Drop 'lost' and the early-stage potential-client stages — the quotes app doesn't surface them.
        .filter(([value]) => value !== 'lost' && !isEarlyStageDeal(value as DealStage))
        .map(([value, label]) => ({
          value,
          label,
          selectedColorClass: STAGE_CHIP_COLORS[value]?.selected,
          unselectedColorClass: STAGE_CHIP_COLORS[value]?.unselected,
        }))
    );
  }, []);

  const quotingStatusOptions = useMemo(() => {
    return Object.entries(DEAL_QUOTING_STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
      selectedColorClass: QUOTING_STATUS_CHIP_COLORS[value as DealQuotingStatus]?.selected,
      unselectedColorClass: QUOTING_STATUS_CHIP_COLORS[value as DealQuotingStatus]?.unselected,
    }));
  }, []);

  const filteredDeals = useMemo(() => {
    return deals.filter((deal: DealWithQuotingStatus) => {
      if (deal.stage === 'lost') return false;
      // Potential-client (lead/qualified) deals belong to the CRM app, not the quotes deal list.
      if (isEarlyStageDeal(deal.stage)) return false;
      if (selectedStages.length > 0 && !selectedStages.includes(deal.stage)) return false;
      if (
        selectedQuotingStatuses.length > 0 &&
        !selectedQuotingStatuses.includes(deal.quotingStatus)
      )
        return false;
      if (search) {
        const q = search.toLowerCase();
        return deal.name.toLowerCase().includes(q) || deal.companyName.toLowerCase().includes(q);
      }
      return true;
    });
  }, [deals, search, selectedStages, selectedQuotingStatuses]);

  // KPI computations
  const stats = useMemo(() => {
    // Keep the KPIs consistent with the list: exclude lost + early-stage potential-client deals.
    const active = deals.filter((d) => d.stage !== 'lost' && !isEarlyStageDeal(d.stage));
    const needAttention = active.filter(
      (d) => d.quotingStatus === 'draft' || d.quotingStatus === 'boq-intake'
    ).length;
    const totalQuoted = active.reduce((sum, d) => sum + (d.quoteTotal || 0), 0);
    const quotedDeals = active.filter((d) => d.quotingStatus === 'quoted');
    const wonDeals = active.filter((d) => d.stage === 'won').length;
    const acceptanceRate =
      quotedDeals.length > 0 ? Math.round((wonDeals / quotedDeals.length) * 100) : 0;
    return {
      activeCount: active.length,
      needAttention,
      totalQuoted,
      acceptanceRate,
      quotedDeals: quotedDeals.length,
    };
  }, [deals]);

  const hasActiveFilters = !!(
    search ||
    selectedStages.length > 0 ||
    selectedQuotingStatuses.length > 0
  );

  const clearFilters = () => {
    setSearch('');
    setSelectedStages([]);
    setSelectedQuotingStatuses([]);
  };

  return (
    <PageLayout>
      <PageHeader
        title="עסקאות"
        subtitle="נהל הצעות מחיר לפי עסקאות CRM"
        action={{
          label: 'הצעה מהירה',
          icon: <FileText size={20} />,
          onClick: () => navigate('/quick-quote'),
        }}
      />

      <div className="mb-5 flex justify-start">
        <DealsViewToggle />
      </div>

      {/* KPI Stats */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="עסקאות פעילות"
          value={dealsLoading ? '...' : stats.activeCount}
          sub={dealsLoading ? '' : `${filteredDeals.length} מוצגות`}
          iconClass="icon-badge-brand"
          icon={Briefcase}
          delay="1"
        />
        <StatCard
          label="ממתינות לטיפול"
          value={dealsLoading ? '...' : stats.needAttention}
          sub={dealsLoading ? '' : 'ללא הערכה'}
          iconClass="icon-badge-orange"
          icon={AlertCircle}
          delay="2"
        />
        <StatCard
          label="שווי הצעות כולל"
          value={
            dealsLoading
              ? '...'
              : stats.totalQuoted > 0
                ? `₪${formatNumberWithCommas(String(Math.round(stats.totalQuoted)))}`
                : '—'
          }
          sub={dealsLoading ? '' : `${stats.quotedDeals} הצעות`}
          iconClass="icon-badge-blue"
          icon={DollarSign}
          delay="3"
        />
        <StatCard
          label="אחוז זכייה"
          value={dealsLoading ? '...' : stats.acceptanceRate > 0 ? `${stats.acceptanceRate}%` : '—'}
          sub={dealsLoading ? '' : 'מהצעות שנשלחו'}
          iconClass="icon-badge-green"
          icon={TrendingUp}
          delay="4"
        />
      </div>

      {error ? (
        <EmptyState
          icon={<AlertCircle size={48} className="text-white" />}
          title="שגיאה בטעינת העסקאות"
          description="אירעה שגיאה בעת טעינת הנתונים. נסה לרענן את הדף."
        />
      ) : dealsLoading ? (
        <div className="table-container animate-stagger-4">
          <TableSkeleton rows={6} columns={8} />
        </div>
      ) : deals.length === 0 ? (
        <EmptyState
          icon={<Briefcase size={48} className="text-white" />}
          title="אין עסקאות"
          description="צור עסקה חדשה ב-CRM כדי להתחיל תהליך תמחור"
        />
      ) : (
        <div className="table-container animate-stagger-4">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="חיפוש לפי שם עסקה או לקוח..."
            chipFilters={[
              {
                id: 'stage',
                label: 'שלב',
                options: stageOptions,
                value: selectedStages,
                onChange: setSelectedStages,
              },
              {
                id: 'quotingStatus',
                label: 'סטטוס תמחור',
                options: quotingStatusOptions,
                value: selectedQuotingStatuses,
                onChange: setSelectedQuotingStatuses,
              },
            ]}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />
          {filteredDeals.length === 0 && hasActiveFilters ? (
            <EmptyState
              dense
              icon={<SearchX size={28} />}
              title="אין עסקאות התואמות לסינון"
              action={{ label: 'נקה סינון', onClick: clearFilters }}
            />
          ) : (
            <DealsTable deals={filteredDeals} />
          )}
        </div>
      )}
    </PageLayout>
  );
}
